import { TCoordinate } from "@api/api.int";
import { GEO_GROPUS, ILocation, isLocationAffair, isLocationGroup, isLocationGuard, useVVStore } from "components/store";
import Feature from "ol/Feature";
import { Point } from "ol/geom";
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import { default as olMap } from 'ol/Map';
import MapBrowserEvent from "ol/MapBrowserEvent";
import MapEvent from "ol/MapEvent";
import OSM from 'ol/source/OSM';
import VectorSource from 'ol/source/Vector';
import { StyleLike } from "ol/style/Style";
import View from 'ol/View';
import { useCallback, useEffect, useRef, useState } from "react";
import { getDistance as sphereGetDistance } from 'ol/sphere';


import { IAffairPub } from "@api/affair.int";
import { IGeoGroupPub } from "@api/geo-group.int";
import { Box } from "@chakra-ui/react";
import { AFFAIR_KINDS_ARR, NEW_LOCATION_IN_CITY_ZOOM, NEW_LOCATION_ZOOM } from "components/appComponents";
import { useOutsideAlerter } from "components/utils";
import { AffairPopup } from "features/affair/AffairPopup";
import { Overlay } from "ol";
import { applyStyle } from 'ol-mapbox-style';
import VectorTileLayer from "ol/layer/VectorTile";
import { fromLonLat, toLonLat } from 'ol/proj.js';
import { useNavigate } from "react-router";
import { iconStyleBySubKind, labelStyleGroup, labelStyleGuard, selectionStyle } from "./styles";

const mapBoxKeyPublic = 'pk.eyJ1IjoidGtyaXpzYSIsImEiOiJjbTN4bHFudWcxZGdvMm5zNzRrY2QxbWd6In0.ZgxgjTThUjUid_UwZpzaHA'
const mapBoxKeyVizvedokHu = 'pk.eyJ1IjoidGtyaXpzYSIsImEiOiJjbTN4bHZoeGswMHppMmpzNTNyZmc4c2FhIn0.v84GD3DzREs6mTLVZ-_uYQ'
const mapBoxKey = 'C3wBO2JoypGffEWFJXNO'

const IS_MAP_BOX = false;
export const HUNGARY_CENTER: TCoordinate = [19.07985, 47.66084];



export interface IViewChangedEvent {
	zoom: number;
	lng1: number;
	lat1: number;
	lng2: number;
	lat2: number;
}

function locationId(loc: ILocation) {
	let id = '????';
	if (isLocationAffair(loc))
		return 'dbAffair::' + loc.dbAffair.affairKey;
	if (isLocationGuard(loc))
		return 'dbGuard::' + loc.dbGuard.userKey;
	if (isLocationGroup(loc))
		return 'dbGroup::' + loc.dbGroup.groupKey;
	return 'new::' + loc.geoLng + '::' + loc.geoLat;
}

export type TMapSelectedItem = IAffairPub | IGeoGroupPub | TCoordinate;

export interface MyMapProps {
	locations: ILocation[];
	onItemClick?: (orig: any) => void;
	onViewChanged?: (evt: IViewChangedEvent) => void;
	selected?: TMapSelectedItem;
}

export function useEventRef<T extends Function>(fn: T): { call: T } {
	const [ref] = useState({
		call: fn
	});
	ref.call = fn;
	return ref;
}

export function MyMap({ locations, onItemClick, onViewChanged, selected, ...rest }: MyMapProps) {

	const store = useVVStore();
	const nav = useNavigate();
	const xOffsetPx = store.isMobile() ? 0 : 370 / 2;
	const yOffsetPx = store.isMobile() ? store.__viewSize[1] / 4 * 3 : 0;

	const [map, setMap] = useState<olMap>();
	const mapElement = useRef<HTMLDivElement>(null);
	const [vectorSource, setVectorSource] = useState<VectorSource<Feature<Point>>>();
	const [vectorLayer, setVectorLayer] = useState<VectorLayer>();
	const [vectorSourceNew, setVectorSourceNew] = useState<VectorSource<Feature<Point>>>();

	const overlayRef = useRef<HTMLDivElement>(null);
	const [overlay, setOverlay] = useState<Overlay>();
	const [popupAffair, setPopupAffair] = useState<IAffairPub>();

	const zoomIn = (center: TCoordinate, zoom?: number) => {
		if (!map)
			return;
		if (!zoom)
			zoom = map.getView().getZoom();
		map.getView().animate({
			zoom,
			duration: 250,
		});
		map.getView().fit(new Point(IS_MAP_BOX ? convertCoordinates(center[0], center[1]) : center), {
			duration: 250,
			maxZoom: zoom,
			padding: [yOffsetPx, 0, 0, xOffsetPx]
		});

	}

	// ================================================== CREATE MAP ==============================================
	useEffect(() => {
		let _map = map;
		if (!_map) {
			// ------------------------------------------------ OVERLAY --------------------------------------
			const _overlay = new Overlay({
				element: overlayRef.current || undefined,
				/*autoPan: {
					animation: {
						duration: 250,
					},
				},*/
			});
			setOverlay(_overlay);
			_overlay.on('change:position', function (evt) {
				var width = document.getElementsByClassName('ol-popup')[0]['offsetWidth'];
				var centerX = -width / 2;
				var position = [centerX, 0];
				_overlay.setOffset(position);
			});

			// ------------------------------------------------ OVERLAY --------------------------------------

			//https://github.com/openlayers/ol-mapbox-style/?tab=readme-ov-file
			const mapBoxLayer = new VectorTileLayer({ declutter: true });
			//applyStyle(mapBoxLayer, 'mapbox://styles/mapbox/outdoors-v12', { accessToken: mapBoxKeyPublic });
			applyStyle(mapBoxLayer, 'mapbox://styles/mapbox/light-v11', { accessToken: mapBoxKeyPublic });
			//applyStyle(mapBoxLayer, 'mapbox://styles/mapbox/dark-v11', { accessToken: mapBoxKeyPublic });

			/*
			var extent = [16.2022982113, 45.7594811061, 22.710531447, 48.6238540716];

			var projection = new Projection({
				code: 'EPSG:4326',
				extent: extent,
				units: 'm'
			});
			*/


			const _vectorSource = new VectorSource<Feature<Point>>({
				features: [],
			});
			setVectorSource(_vectorSource);
			const _vectorLayer = new VectorLayer({
				source: _vectorSource,
			});
			setVectorLayer(_vectorLayer);
			const _vectorSourceNew = new VectorSource<Feature<Point>>({
				features: [],
			});
			setVectorSourceNew(_vectorSourceNew);
			const _vectorLayerNew = new VectorLayer({
				source: _vectorSourceNew,
			});



			_map = new olMap({
				target: mapElement.current || undefined,
				layers: [

					// USGS Topo
					//mbLayer,
					...IS_MAP_BOX ? [mapBoxLayer] : [new TileLayer({ source: new OSM() })],
					//
					_vectorLayer,
					_vectorLayerNew,
				],
				overlays: [_overlay],
				view: new View({
					projection: IS_MAP_BOX
						? 'EPSG:3857'
						: 'EPSG:4326',
					//: projection,

					center: IS_MAP_BOX
						? fromLonLat(HUNGARY_CENTER)
						: HUNGARY_CENTER,
					zoom: 12
				}),
				controls: [],
				moveTolerance: 5,
			});


			setMap(_map);
		}
		_map.setTarget(mapElement.current || undefined);
		return () => _map!.setTarget(undefined);
	}, [map, mapElement.current]);

	// ================================================== Style for Location ==============================================
	const getLocationStyle = (loc: ILocation, isSugg = false): StyleLike => {

		//const affairStyle = iconStyleBySubKind['figyelo'];
		let affairStyle = iconStyleBySubKind[loc.subKind];
		// TODO: temp!
		if (!affairStyle)
			affairStyle = iconStyleBySubKind['20'];

		if (isLocationGroup(loc)) {
			const isLoading = store.loadingMunicipalities.indexOf(loc.dbGroup.groupKey) !== -1;
			let color = '#008CC6';
			let top = 
				AFFAIR_KINDS_ARR.map(ak => ({ color: ak.color, db: loc.dbGroup.sumInfo['affairCount_' + ak.key] }))
					.sort((a,b) => a.db - b.db).pop();
			if (top && top.db >= loc.dbGroup.sumInfo.guardCount)
				color = top?.color

			const ls = labelStyleGroup(color); //'#008CC6'
			ls.getText()?.setText(isLoading ? 'loading...' : ('' + ((loc.dbGroup!.sumInfo.affairCount || 0) + (loc.dbGroup!.sumInfo.guardCount || 0))));
			return [affairStyle, ls];

		} else if (loc.kind === 'Guard') {
			if (isLocationGuard(loc)) {
				const ls = labelStyleGuard.clone()
				ls.getText()?.setText(loc.dbGuard.guard?.name || '???');
				return [affairStyle, ls];
			} else if (loc.isNew) {
				const ls = labelStyleGuard.clone()
				ls.getText()?.setText(loc.title || '???');
				return [affairStyle, ls];
			} else {
				return [affairStyle];
			}
		} else if ((isLocationAffair(loc) && (loc.dbAffair === selected || isSugg)) || loc.isNew) {
			return [affairStyle, selectionStyle];
		} else {
			return [affairStyle];
		}
	}


	// ================================================== APPEND ITEMS ==============================================
	useEffect(() => {
		//console.log('MAP:appendItems()');
		if (!map || !vectorSource)
			return;

		const newMap = new Map<string, ILocation>();
		const existingMap = new Map<string, { location: ILocation, feature: Feature }>();

		let locs: ILocation[];
		locs = locations;

		for (let loc of locs) {
			newMap.set(locationId(loc), loc);
		}

		vectorSource.forEachFeature(f => {
			//console.log('???', f);
			const loc = f.get('loc') as ILocation;
			const id = locationId(loc);
			existingMap.set(id, { location: loc, feature: f });
		});



		existingMap.forEach((ex, locId) => {

			const newLoc = newMap.get(locId);
			if (!newLoc) { // ----------------------------- remove features -----------------------------
				vectorSource.removeFeature(ex.feature as Feature<any>);
			} else { // ----------------------------- update features -----------------------------
				ex.feature.setStyle(getLocationStyle(newLoc));
				ex.feature.setGeometry(new Point([newLoc.geoLng, newLoc.geoLat]))
				/*const oldLoc = ex.feature.get('loc') as ILocation;
				if (oldLoc && isLocationGuard(oldLoc) && oldLoc.dbGuard.userKey === 'usr1742121948396') {
					console.log('-----> set tkrizsa', newLoc);
					ex.feature.set('loc', newLoc);
				}*/
			}



		});


		newMap.forEach((loc, locId) => {
			if (existingMap.get(locId)) {
				//console.log('megvan');
				return;
			}

			// ----------------------------- add new feature -----------------------------
			var feature = IS_MAP_BOX
				? new Feature(new Point(convertCoordinates(loc.geoLng, loc.geoLat)))
				: new Feature(new Point([loc.geoLng, loc.geoLat]))
			feature.set('loc', loc);
			vectorSource.addFeature(feature);
			feature.setStyle(getLocationStyle(loc));

		});

		adjustMarkerPositions(vectorSource.getFeatures(), 40, map);

	}, [map, locations, selected, store.mapMode, store.loadingMunicipalities]);

	// ================================================== APPEND ITEMS NEW ==============================================
	useEffect(() => {
		if (!map || !vectorSourceNew)
			return;

		//console.log('**** CLEAR BY SUGGESTIONS *****', store.suggestionList.length);
		vectorSourceNew.clear();

		store.suggestionList.forEach((loc) => {
			var feature = IS_MAP_BOX
				? new Feature(new Point(convertCoordinates(loc.geoLng, loc.geoLat)))
				: new Feature(new Point([loc.geoLng, loc.geoLat]))
			feature.set('loc', loc);
			feature.setStyle(getLocationStyle(loc, true));
			vectorSourceNew.addFeature(feature);
			//console.log('---->', loc);
		});


	}, [map, store.suggestionList, vectorSourceNew]);

	useEffect(() => {
		if (!map || store.suggestionList.length <= 0)
			return;
		const loc = store.suggestionList[0];
		const zoom = loc.kind === 'FindCity' ? NEW_LOCATION_IN_CITY_ZOOM : NEW_LOCATION_ZOOM;
		zoomIn([loc.geoLng, loc.geoLat], zoom);
	}, [map, store.suggestionList]);

	useEffect(() => {
		console.log('**** ', store.mapMode);
		if (store.mapMode === 'SELECT') {
			vectorLayer?.setOpacity(.45);
		} else {
			vectorLayer?.setOpacity(1);
			console.log('**** CLEAR BY MAPMODE *****');
			vectorSourceNew?.clear();
		}
	}, [store.mapMode, vectorLayer, vectorSourceNew])


	// ================================================== MOUSE HANDLER ==============================================
	useEffect(() => {
		if (!map)
			return;

		// ------------------------------------- click -----------------------------------------
		const onClick = (evt: MapBrowserEvent<any>) => {
			//console.log('click');
			store.setSuggestionList([]);
			setPopupAffair(undefined);
			overlay!.setPosition(undefined);

			// --------------------------------- Hely kiválasztás (bejelent/elhelyez) -----------
			if (store.mapMode === 'SELECT') {
				if (evt.coordinate && evt.coordinate.length === 2) {
					let c: TCoordinate;
					//console.log('BEFORE', evt.coordinate);

					if (IS_MAP_BOX) {
						c = toLonLat(evt.coordinate) as TCoordinate;
					} else {
						c = evt.coordinate as TCoordinate;
					}
					//c = map.getCoordinateFromPixel(evt.pixel) as TCoordinate;
					//console.log('AFTER', c);
					store.setCreateLngLat(c);
					//nav(`/create`);
				}
				return;
			}

			// --------------------------------- Feature click -----------
			var feature = map.forEachFeatureAtPixel(evt.pixel, function (feat: any, layer: any) {
				return feat;
			}, { hitTolerance: 6 }
			);
			let p = feature ? feature.get('loc') as ILocation : undefined;
			if (p && isLocationAffair(p)) { // ----------------- Ügy ----------

				const itemCoords = map.getCoordinateFromPixel([evt.pixel[0], evt.pixel[1]])
				const coordinate = evt.coordinate;
				if (!overlay || store.isMobile()) {
					if (onItemClick)
						onItemClick(p.dbAffair);
				} else {
					setPopupAffair(p.dbAffair);
					overlay.setPosition(coordinate);

				}

				zoomIn(itemCoords as TCoordinate)
			} else if (p && isLocationGuard(p)) { // ----------------- Vízvédő ----------
				const coords = map.getCoordinateFromPixel([evt.pixel[0], evt.pixel[1]])
				zoomIn(coords as TCoordinate);
				nav(store.mapNavRoot + '/guard/' + p.dbGuard.userKey);

			} else if (p && isLocationGroup(p)) { // ----------------- Város/Járás ----------
				const g = p;
				const geoGroupIx = GEO_GROPUS.findIndex(gg => gg.geoGroup === g.dbGroup.geoGroupLevel);

				if (geoGroupIx > 0) {
					console.log(store.mapNavRoot);
					if (store.mapNavRoot.startsWith('/group')) {
						nav('/group/region/' + p.dbGroup.groupKey)
					} else {
						const geoGroup = GEO_GROPUS[geoGroupIx];
						zoomIn([p.geoLng, p.geoLat], geoGroup!.zoomIn);
					}
				}

			}
		}
		map.on('click', onClick);

		// ---- pointermove ----
		const onPointerMove = (evt: MapBrowserEvent<any>) => {
			if (store.mapMode === 'SELECT') {
				map.getTargetElement().style.cursor = 'pointer'; //'crosshair';
				return;
			}
			var loc = map.forEachFeatureAtPixel(evt.pixel, function (feat: any, layer: any) {
				const loc = feat.get('loc');
				if (loc)
					return loc;
			}, { hitTolerance: 6 }
			) as ILocation;


			let cursor = '';
			if (loc) {
				cursor = 'pointer';
			} else {
				cursor = ''
			}
			map.getTargetElement().style.cursor = cursor;

		};
		map.on('pointermove', onPointerMove);

		// ---- viewChanged ----
		const onMoveEnd = (evt: MapEvent) => {
			store.setMapMoveIntoView(undefined);
			if (onViewChanged) {
				const view = map.getView();
				const coords = map.getView().calculateExtent(map.getSize());
				if (!IS_MAP_BOX) {
					onViewChanged({
						zoom: view.getZoom() || 7,
						lng1: coords[0],
						lat1: coords[1],
						lng2: coords[2],
						lat2: coords[3]
					});
				} else {
					onViewChanged({
						zoom: view.getZoom() || 7,
						lng1: convertCoordinatesBack(coords[0], coords[1])[0],
						lat1: convertCoordinatesBack(coords[0], coords[1])[1],
						lng2: convertCoordinatesBack(coords[2], coords[3])[0],
						lat2: convertCoordinatesBack(coords[2], coords[3])[1]
					});
				}
			}
		}
		map.on('moveend', onMoveEnd);

		return () => {
			map.un('click', onClick);
			map.un('pointermove', onPointerMove);
			map.un('moveend', onMoveEnd);
		}
	}, [map, store.mapMode, onItemClick]);

	useEffect(() => {
		if (map && store.mapMoveIntoView?.center && store.mapMoveIntoView?.zoom) {
			let p = { geoLng: store.mapMoveIntoView.center[0], geoLat: store.mapMoveIntoView.center[1] };
			let center = IS_MAP_BOX ? convertCoordinates(p.geoLng, p.geoLat) : [p.geoLng, p.geoLat];
			console.log('-> center0', center);
			let pc = map.getPixelFromCoordinate(center);
			console.log('-> pc0', pc);
			console.log('-> offset', xOffsetPx, yOffsetPx);
			console.log('-> center', center);
			zoomIn(center as TCoordinate, store.mapMoveIntoView.zoom);
		}
	}, [store.mapMoveIntoView?.center, store.mapMoveIntoView?.zoom, map]);


	const clickedOutside = useCallback(() => {
		overlay!.setPosition(undefined);
	}, [overlay]);
	useOutsideAlerter(mapElement, clickedOutside);


	return (<>
		<div ref={mapElement as any} className="map-container" id='map' style={{ width: '100vw', height: '100vh', overflow: "hidden" }}></div>
		<Box ref={overlayRef} className='ol-popup'
			position='absolute'
			bottom='35px'
		>
			{popupAffair && <AffairPopup affair={popupAffair} onClose={clickedOutside} />}
		</Box>
		<Box position='fixed' left={0} bottom={0} bg='black' fontSize='sm' p={1} gap={1} display='none' flexDir='column'>
			{/*map?.getView().getZoom()} | {yOffsetPx*/}
			{store.mapNavRoot}
		</Box>

	</>
	);
}

// FROM : lat/lon (EPSG:4326) TO: Spherical mercator ( EPSG:3857 )
function convertCoordinates(lon, lat) {
	lon = parseFloat(lon);
	lat = parseFloat(lat);
	var x = (lon * 20037508.34) / 180;
	var y = Math.log(Math.tan(((90 + lat) * Math.PI) / 360)) / (Math.PI / 180);
	y = (y * 20037508.34) / 180;
	//console.log('CONVERT', lon, lat, x, y);
	return [x, y];
}

function convertCoordinatesBack(lon3857, lat3857) {
	const e = 2.7182818284
	const X = 20037508.34
	//converting the logitute from epsg 3857 to 4326
	const lon4326 = (lon3857 * 180) / X;

	//converting the latitude from epsg 3857 to 4326 split in multiple lines for readability

	let lat4326 = lat3857 / (X / 180)
	const exponent = (Math.PI / 180) * lat4326

	lat4326 = Math.atan(e ** exponent)
	lat4326 = lat4326 / (Math.PI / 360)
	lat4326 = lat4326 - 90
	return [lon4326, lat4326]
}


function adjustMarkerPositions(features, minPixelDistance, map) {
	const view = map.getView();
	const resolution = view.getResolution(); // Get current map resolution (meters per pixel)

	// Convert pixel distance to map units (meters)
	const minDistanceInUnits = minPixelDistance * resolution;

	for (let i = 0; i < features.length; i++) {
		let featureA = features[i];
		let coordsA = featureA.getGeometry().getCoordinates();

		for (let j = i + 1; j < features.length; j++) {
			let featureB = features[j];
			let coordsB = featureB.getGeometry().getCoordinates();

			// Calculate the distance between the two markers in map units
			let distance = sphereGetDistance(coordsA, coordsB) * resolution;
			//console.log('--- ADJUST CHECK ---', distance , minDistanceInUnits)
			if (distance < minDistanceInUnits) {
				const from = [...coordsB];
				// Calculate the angle between the two markers
				let angle = Math.atan2(coordsB[1] - coordsA[1], coordsB[0] - coordsA[0]);

				// Offset the second marker by the difference in distance
				let offsetX = Math.cos(angle) * (minDistanceInUnits - distance);
				let offsetY = Math.sin(angle) * (minDistanceInUnits - distance);

				// Apply the offset to the second marker
				coordsB[0] += offsetX;
				coordsB[1] += offsetY;

				featureB.getGeometry().setCoordinates(coordsB);
				console.log('ADJUST', ...from, ...coordsB, coordsB[0] - from[0], coordsB[1] - from[1]);
			}
		}
	}
}
